/**
 * Shared location-color helpers.
 *
 * Every calendar surface (Today, Class Sessions, Class Schedules, All
 * Classes, staff/student calendars, Attendance, Makeup, agenda/list views)
 * MUST resolve event styling through this module so a change to a
 * location's color in Settings propagates everywhere on the next refresh
 * without touching per-page code.
 *
 * The color represents the LOCATION only. Status (cancelled/completed/
 * makeup/unpaid) belongs to badges/icons layered on top — never to the
 * main color. class_mode is honoured because an online-only class has no
 * physical location; a hybrid class does, so it uses the physical color.
 */

/** Blue fallback for online-only classes with no physical location. */
export const ONLINE_LOCATION_COLOR = "#1976d2";

/** Neutral slate for anything else that never resolved a color. */
export const FALLBACK_LOCATION_COLOR = "#64748b";

export type ClassMode = "Online" | "Offline" | "InPerson" | "Hybrid" | string | null | undefined;

/**
 * Rules:
 *  - Online-only class + no location → ONLINE_LOCATION_COLOR
 *  - Hybrid, InPerson, or any class WITH a location → the location's own color
 *  - Any location that has no configured color → FALLBACK_LOCATION_COLOR
 *  - Invalid color strings fall back too — never returns undefined
 */
export function resolveLocationColor(
  locationColor: string | null | undefined,
  classMode?: ClassMode
): string {
  const modeStr = (classMode || "").toString().toLowerCase();
  const isOnlineOnly = modeStr === "online";
  if (isOnlineOnly && !locationColor) return ONLINE_LOCATION_COLOR;
  if (!locationColor || typeof locationColor !== "string") return FALLBACK_LOCATION_COLOR;
  const trimmed = locationColor.trim();
  if (!trimmed) return FALLBACK_LOCATION_COLOR;
  return trimmed;
}

/**
 * Convert `#rrggbb` (with or without the leading #) plus an alpha 0..1 to
 * an `rgba(r,g,b,a)` string. Accepts short (`#rgb`) hex too. Returns the
 * original string if it isn't a hex value (e.g. `rgb(...)`, `hsl(...)`) so
 * callers can pass user-supplied colours safely.
 */
export function withAlpha(color: string, alpha: number): string {
  if (!color) return color;
  const c = color.trim();
  const m3 = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(c);
  const m6 = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(c);
  let r: number | null = null;
  let g: number | null = null;
  let b: number | null = null;
  if (m6) {
    r = parseInt(m6[1], 16);
    g = parseInt(m6[2], 16);
    b = parseInt(m6[3], 16);
  } else if (m3) {
    r = parseInt(m3[1] + m3[1], 16);
    g = parseInt(m3[2] + m3[2], 16);
    b = parseInt(m3[3] + m3[3], 16);
  }
  if (r === null || g === null || b === null) return c;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r},${g},${b},${a})`;
}

/**
 * The canonical calendar-event style bundle. Every calendar renders events
 * from this so the visual language is consistent:
 *   - `background`: 14% tint of the location color (readable on light bg)
 *   - `border`: full-strength location color on the left (4px accent stripe)
 *   - `dot` / `accent`: full-strength for status dots + top borders
 *   - `text`: near-black; readable on the pale tint
 *   - `mutedText`: secondary text like time/teacher name
 *
 * For status decoration (cancelled/makeup/etc.) callers layer badges/icons
 * OVER these values — do NOT swap `background` or `border` for status.
 */
export interface LocationEventColors {
  background: string;
  border: string;
  accent: string;
  dot: string;
  text: string;
  mutedText: string;
}

export function getLocationEventColors(
  locationColor: string | null | undefined,
  classMode?: ClassMode
): LocationEventColors {
  const base = resolveLocationColor(locationColor, classMode);
  return {
    background: withAlpha(base, 0.14),
    border: base,
    accent: base,
    dot: base,
    text: "#0f172a",
    mutedText: "#475569",
  };
}

/**
 * A single legend entry, built from the shared locations list. Callers
 * derive `entries` from the active locations they display; the "Online"
 * entry is appended once when any online-only event is present.
 */
export interface LegendEntry {
  key: string;
  label: string;
  color: string;
}

export function buildLegendEntries(
  locations: Array<{ id: number; location_name?: string; location_color?: string | null }>,
  { includeOnline }: { includeOnline?: boolean } = {}
): LegendEntry[] {
  const entries: LegendEntry[] = (locations || [])
    .filter((l) => l && l.location_name)
    .map((l) => ({
      key: `loc-${l.id}`,
      label: l.location_name as string,
      color: resolveLocationColor(l.location_color),
    }));
  if (includeOnline) {
    entries.push({ key: "online", label: "Online", color: ONLINE_LOCATION_COLOR });
  }
  return entries;
}
